export const bare = (text: string): string => text.normalize("NFD").replace(/\p{Mn}/gu, "");

const FENCE = /(^|\n)[ \t]*(```|~~~)[^\n]*\n[\s\S]*?(?:\n[ \t]*\2[^\n]*|$)/g;
const CODE_SPAN = /`[^`\n]*`/g;
const QUOTED_LINE = /^[ \t]*>[^\n]*/gm;
const QUOTATION = /"[^"\n]*"|\u201c[^\u201d\n]*\u201d/g;

export function whatIsSaid(text: string): string {
  let said = text;
  for (const rx of [FENCE, CODE_SPAN, QUOTED_LINE, QUOTATION]) {
    said = said.replace(rx, (match) => " ".repeat(match.length));
  }
  return said;
}

const MENTION = /(^|[^A-Za-z0-9_@./+-])@([a-z][a-z0-9]*(?:-[a-z0-9]+)*)(?![A-Za-z0-9_-])/gi;

export function mentionsIn(text: string): Set<string> {
  const found = new Set<string>();
  for (const match of whatIsSaid(text).matchAll(MENTION)) {
    if (match[2]) found.add(match[2].toLowerCase());
  }
  return found;
}

const wholeWord = (word: string, after = "(?![a-z0-9])"): RegExp =>
  new RegExp(`(^|[^a-z0-9])(?:${word})${after}`);

const escaped = (word: string): string => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const names = (said: string, word: string): boolean => wholeWord(escaped(word)).test(said);

export const VOCATIVES = [
  "papa",
  "maman",
  "mama",
  "papi",
  "mamie",
  "tonton",
  "tata",
  "cheri",
  "cherie",
  "fiston",
  "ma puce",
  "mon coeur",
  "les enfants",
  "tout le monde",
  "papounet",
] as const;

const VOCATIVE = wholeWord(
  [...VOCATIVES]
    .sort((a, b) => b.length - a.length)
    .map(escaped)
    .join("|"),
);

export const vocativeIn = (said: string): boolean => VOCATIVE.test(said);

const BROADCAST = wholeWord(
  "tout le monde|tous|toutes|quelqu un|quelqu|chacun|vous tous|everyone|anyone|somebody|anybody",
);

export const broadcastIn = (said: string): boolean => BROADCAST.test(said);

const THIRD_PARTY = "a|de|du|que|qui|par|avec|pour|chez|comme|demande a|demander a|dit|dis";

export const talkedAbout = (said: string, name: string): boolean =>
  wholeWord(`(?:${THIRD_PARTY})\\s+${escaped(name)}`).test(said);

const TASK_VERB = wholeWord(
  "cherche|cherches|trouve|trouves|recherche|resume|resumes|calcule|calcules|traduis|traduits|" +
    "liste|listes|compare|convertis|cree|crees|ecris|ecrit|sauvegarde|enregistre|ajoute|supprime|" +
    "efface|renomme|reserve|verifie|verifies|explique|expliques|donne-moi|search|find|summarise|" +
    "summarize|translate|calculate|create|write|save|list",
);

const FILE_VERB = wholeWord(
  "cree|crees|ecris|ecrit|sauvegarde|enregistre|ajoute|supprime|efface|renomme|fichier|" +
    "create|write|save|file|delete|rename",
);

export const taskVerbIn = (said: string): boolean => TASK_VERB.test(said);
export const fileVerbIn = (said: string): boolean => FILE_VERB.test(said);

export const toolish = (handle: string): boolean => /tool|file|shell|code|coco/i.test(handle);

const ACKS = new Set([
  "ok",
  "okay",
  "oki",
  "dac",
  "daccord",
  "ah",
  "ahah",
  "haha",
  "hahaha",
  "lol",
  "mdr",
  "merci",
  "mercii",
  "thanks",
  "thx",
  "ty",
  "bien",
  "super",
  "parfait",
  "cool",
  "nickel",
  "top",
  "bravo",
  "oui",
  "non",
  "yes",
  "no",
  "yep",
  "nope",
  "ouais",
  "bof",
  "hmm",
  "mouais",
  "ca marche",
  "tres bien",
  "genial",
  "impec",
  "ok merci",
  "merci beaucoup",
  "bisous",
  "a plus",
  "bonne nuit",
]);

const ACK_WORDS = new Set([
  "ok",
  "okay",
  "oki",
  "dac",
  "daccord",
  "accord",
  "d",
  "ah",
  "oh",
  "ahah",
  "haha",
  "hahaha",
  "lol",
  "mdr",
  "merci",
  "mercii",
  "thanks",
  "thx",
  "ty",
  "bien",
  "super",
  "parfait",
  "cool",
  "nickel",
  "top",
  "bravo",
  "genial",
  "impec",
  "excellent",
  "ouais",
  "bof",
  "hmm",
  "mouais",
  "tres",
  "beaucoup",
  "vu",
  "noted",
  "nice",
]);

const AN_ACK_HOLDS = 3;

export function isAnAck(text: string): boolean {
  const flat = bare(text.toLowerCase())
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (ACKS.has(flat)) return true;
  if (text.includes("?")) return false;
  const words = flat.split(" ").filter(Boolean);
  return words.length > 0 && words.length <= AN_ACK_HOLDS && words.every((word) => ACK_WORDS.has(word));
}

export const endsInAQuestion = (text: string): boolean => /\?\s*$/.test(text.trimEnd());
